import React, { useLayoutEffect, useRef } from 'react';
import { AppBar, Toolbar, IconButton, Box, Tooltip, Divider, Typography } from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import { Link } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import LogoutIcon from '@mui/icons-material/LogoutOutlined';
import NotificationBell from '../notifications/NotificationBell';
import { tokens } from '../../styles/tokens';

// The sidebar's collapsed rail. The logo sits centred over it, on the same
// line as the navigation icons below, and the name follows.
const RAIL = tokens.sizes.railWidth;

const TopBar = ({ onDrawerToggle, slotRef }) => {
    const { logout } = useAuth();
    const barRef = useRef(null);

    // publish the header's height (one row, or two when a page fills the slot)
    useLayoutEffect(() => {
        const el = barRef.current;
        if (!el) return undefined;
        const publish = () => document.documentElement.style.setProperty('--header-h', `${el.offsetHeight}px`);
        publish();
        const ro = new ResizeObserver(publish);
        ro.observe(el);
        return () => ro.disconnect();
    }, []);

    return (
        <AppBar
            ref={barRef}
            position="fixed"
            elevation={0}
            // Spans the page above the sidebar: one bar carries the logo, the
            // name and the account actions, rather than the logo in the sidebar
            // and the name beside it across a border.
            sx={{
                width: '100%',
                zIndex: (theme) => theme.zIndex.drawer + 1,
                backgroundColor: '#ffffff',
                borderBottom: '1px solid #E2E8F0',
            }}
        >
            <Toolbar
                sx={{
                    minHeight: `${tokens.sizes.topbarHeight}px !important`,
                    pl: { xs: 2, md: 0 },
                    // the sign-out glyph, not its button, ends on the page's right edge
                    pr: { xs: 2, md: '17px' },
                    justifyContent: 'flex-end',
                }}
            >
                {/* Left side - Mobile menu only */}
                <Box sx={{ display: { xs: 'flex', md: 'none' }, mr: 1 }}>
                    <IconButton
                        aria-label="open drawer"
                        edge="start"
                        onClick={onDrawerToggle}
                        sx={{
                            color: '#475569',
                            '&:hover': {
                                backgroundColor: '#F1F5F9',
                            },
                        }}
                    >
                        <MenuIcon />
                    </IconButton>
                </Box>

                <Box
                    component={Link}
                    to="/dashboard"
                    aria-label="LightBeam Watchtower, home"
                    sx={{ display: 'flex', alignItems: 'center', mr: 'auto', textDecoration: 'none', userSelect: 'none' }}
                >
                    <Box sx={{ width: { xs: 'auto', md: RAIL }, display: 'flex', justifyContent: 'center', mr: { xs: 1.25, md: 0 } }}>
                        <Box component="img" src={`${process.env.PUBLIC_URL}/lightbeam-logo.svg`} alt="" sx={{ height: 24, display: 'block' }} />
                    </Box>
                    <Typography component="span" sx={{ fontSize: '0.9375rem', fontWeight: 700, color: '#0F172A', letterSpacing: '-0.01em' }}>
                        LightBeam
                    </Typography>
                    <Typography component="span" sx={{ ml: 0.75, fontSize: '0.9375rem', fontWeight: 500, color: '#64748B', letterSpacing: '-0.01em' }}>
                        Watchtower
                    </Typography>
                </Box>

                {/* Right side - Actions */}
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                    }}
                >
                    <NotificationBell />

                    <Divider orientation="vertical" flexItem sx={{ mx: 0.5, height: 18, alignSelf: 'center', borderColor: '#E2E8F0' }} />

                    <Tooltip title="Sign out" arrow>
                        <IconButton
                            onClick={logout}
                            size="small"
                            sx={{
                                color: '#64748B',
                                borderRadius: 1.5,
                                width: 30,
                                height: 30,
                                transition: 'all 0.15s ease',
                                '&:hover': {
                                    backgroundColor: '#FEE2E2',
                                    color: '#DC2626',
                                },
                            }}
                        >
                            <LogoutIcon sx={{ fontSize: 17 }} />
                        </IconButton>
                    </Tooltip>
                </Box>
            </Toolbar>
            {/* The page's tabs, on a second row that starts under the name, so
                the header's two rows share one left edge. Empty on most pages. */}
            <Box ref={slotRef} sx={{
                pl: { xs: 2, md: `${RAIL}px` },
                pr: 3,
                '&:empty': { display: 'none' },
            }} />
        </AppBar>
    );
};

export default TopBar;
